import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { 
  Building2, Users, MapPin, Filter, ArrowLeft, CheckCircle, 
  Search, ChevronDown, ExternalLink
} from 'lucide-react';

const VerifiedPartnersPage = () => {
  const { type } = useParams();
  const navigate = useNavigate();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ sector: 'all', state: 'all', search: '' });

  // Refined Color Palette - Blackish Green Button System
  const colors = {
    sageTeal: '#97B6A7',
    softTeal: '#759B89',
    mutedTeal: '#587F6C',
    forestTeal: '#0E1E19',
    buttonPrimary: '#0E1E19',
    buttonHover: '#1A2F28',
    gold: '#D6B15E',
    goldHover: '#E2C77A',
    textHeading: '#FFFFFF',
    textBody: '#EAF2EF',
    textMuted: '#BFD0C9',
    cardLight: 'rgba(255, 255, 255, 0.92)',
  };

  const pathname = window.location.pathname;
  const isSponsor = pathname.includes('sponsors');
  const pageTitle = isSponsor ? 'Verified Sponsor Companies' : 'Verified NGOs & Agencies';
  const pageDescription = isSponsor 
    ? 'Corporate partners committed to impactful CSR initiatives across India'
    : 'Implementing agencies verified for credibility, compliance, and capacity';

  const allSectors = ['Education', 'Healthcare', 'Skill Development', 'Women Empowerment', 'Rural Development', 'Environment', 'Livelihood', 'Sanitation'];
  const allStates = ['Maharashtra', 'Karnataka', 'Delhi', 'Gujarat', 'Tamil Nadu', 'Rajasthan', 'Uttar Pradesh', 'West Bengal', 'Kerala', 'Madhya Pradesh', 'Andhra Pradesh', 'Telangana'];

  useEffect(() => {
    fetchListings();
  }, [pathname]);

  const fetchListings = async () => {
    setLoading(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const endpoint = isSponsor ? '/api/xchange/listings/corporate' : '/api/xchange/listings/ngo';
      const response = await fetch(`${backendUrl}${endpoint}`);
      if (response.ok) {
        const data = await response.json();
        setListings(data);
      }
    } catch (err) {
      console.error('Error fetching listings:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredListings = listings.filter(listing => {
    if (filters.sector !== 'all' && !listing.sectors?.includes(filters.sector)) return false;
    if (filters.state !== 'all' && !listing.states?.includes(filters.state)) return false;
    if (filters.search && !listing.orgName?.toLowerCase().includes(filters.search.toLowerCase())) return false;
    return true;
  });

  const listingsByState = filteredListings.reduce((acc, listing) => {
    const primaryState = listing.states?.[0] || 'Pan India';
    if (!acc[primaryState]) acc[primaryState] = [];
    acc[primaryState].push(listing);
    return acc;
  }, {});

  const inputStyle = { 
    backgroundColor: 'rgba(14, 30, 25, 0.6)', 
    border: '1px solid rgba(151, 182, 167, 0.3)', 
    color: colors.textBody 
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: colors.forestTeal }}>
      <Header />

      {/* Hero Section */}
      <section 
        className="relative pt-24 pb-8 px-4 overflow-hidden"
        style={{ 
          background: `linear-gradient(135deg, ${colors.sageTeal} 0%, ${colors.softTeal} 35%, ${colors.mutedTeal} 70%, ${colors.forestTeal} 100%)`
        }}
      >
        <div 
          className="absolute inset-0 opacity-5"
          style={{ 
            backgroundImage: 'radial-gradient(circle at 2px 2px, rgba(255,255,255,0.3) 1px, transparent 0)',
            backgroundSize: '32px 32px'
          }}
        />
        <div className="max-w-6xl mx-auto relative z-10">
          <button 
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-sm mb-4 transition-colors"
            style={{ color: colors.textBody }}
            onMouseEnter={(e) => e.currentTarget.style.color = colors.gold}
            onMouseLeave={(e) => e.currentTarget.style.color = colors.textBody}
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </button>

          <div className="flex items-center gap-3 mb-4">
            <div 
              className="w-12 h-12 rounded-xl flex items-center justify-center"
              style={{ backgroundColor: 'rgba(214, 177, 94, 0.15)' }}
            >
              {isSponsor ? <Building2 className="w-5 h-5" style={{ color: colors.gold }} /> : <Users className="w-5 h-5" style={{ color: colors.gold }} />}
            </div>
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold" style={{ color: colors.textHeading }}>
                {pageTitle}
              </h1>
              <p className="text-sm mt-1" style={{ color: colors.textBody }}>{pageDescription}</p>
            </div>
          </div>

          {/* Stats */}
          <div className="flex items-center gap-6 mt-6">
            <div className="flex items-center gap-2">
              <span className="text-2xl font-bold" style={{ color: colors.gold }}>{listings.length}</span>
              <span className="text-sm" style={{ color: colors.textBody }}>Total Partners</span>
            </div>
            <div className="w-px h-6" style={{ backgroundColor: 'rgba(255, 255, 255, 0.3)' }}></div>
            <div className="flex items-center gap-2">
              <span className="text-2xl font-bold" style={{ color: colors.gold }}>{Object.keys(listingsByState).length}</span>
              <span className="text-sm" style={{ color: colors.textBody }}>States Covered</span>
            </div>
          </div>
        </div>
      </section>

      {/* Filters */}
      <section 
        className="py-4 px-4 sticky top-16 z-30"
        style={{ 
          backgroundColor: colors.mutedTeal,
          borderBottom: '1px solid rgba(151, 182, 167, 0.2)'
        }}
      >
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: colors.textMuted }} />
              <input
                type="text"
                placeholder="Search by organization name..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                className="w-full h-12 pl-10 pr-4 rounded-lg text-sm transition-all outline-none"
                style={inputStyle}
                onFocus={(e) => { e.target.style.borderColor = colors.gold; }}
                onBlur={(e) => { e.target.style.borderColor = 'rgba(151, 182, 167, 0.3)'; }}
              />
            </div>

            {/* Sector Filter */}
            <div className="w-full md:w-48">
              <select
                value={filters.sector}
                onChange={(e) => setFilters({ ...filters, sector: e.target.value })}
                className="w-full h-12 px-4 rounded-lg text-sm transition-all outline-none cursor-pointer"
                style={inputStyle}
              >
                <option value="all">All Sectors</option>
                {allSectors.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>

            {/* State Filter */}
            <div className="w-full md:w-48">
              <select
                value={filters.state}
                onChange={(e) => setFilters({ ...filters, state: e.target.value })}
                className="w-full h-12 px-4 rounded-lg text-sm transition-all outline-none cursor-pointer"
                style={inputStyle}
              >
                <option value="all">All States</option>
                {allStates.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>

          {/* Active Filters */}
          {(filters.sector !== 'all' || filters.state !== 'all' || filters.search) && (
            <div className="flex items-center gap-2 mt-3">
              <span className="text-xs" style={{ color: colors.textMuted }}>Active filters:</span>
              {filters.search && (
                <span className="px-2 py-1 rounded text-xs" style={{ backgroundColor: 'rgba(214, 177, 94, 0.2)', color: colors.gold }}>Search: {filters.search}</span>
              )}
              {filters.sector !== 'all' && (
                <span className="px-2 py-1 rounded text-xs" style={{ backgroundColor: 'rgba(214, 177, 94, 0.2)', color: colors.gold }}>{filters.sector}</span>
              )}
              {filters.state !== 'all' && (
                <span className="px-2 py-1 rounded text-xs" style={{ backgroundColor: 'rgba(214, 177, 94, 0.2)', color: colors.gold }}>{filters.state}</span>
              )}
              <button 
                onClick={() => setFilters({ sector: 'all', state: 'all', search: '' })}
                className="text-xs underline"
                style={{ color: colors.textMuted }}
              >
                Clear all
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Listings */}
      <section className="py-8 px-4" style={{ backgroundColor: colors.softTeal }}>
        <div className="max-w-6xl mx-auto">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: colors.gold, borderTopColor: 'transparent' }}></div>
            </div>
          ) : filteredListings.length === 0 ? (
            <div className="text-center py-20">
              <div 
                className="w-16 h-16 rounded-xl mx-auto mb-4 flex items-center justify-center"
                style={{ backgroundColor: 'rgba(214, 177, 94, 0.15)' }}
              >
                <Filter className="w-8 h-8" style={{ color: colors.gold }} />
              </div>
              <h3 className="text-lg font-semibold mb-2" style={{ color: colors.textHeading }}>No partners found</h3>
              <p className="text-sm" style={{ color: colors.textMuted }}>Try adjusting your filters to see more results.</p>
            </div>
          ) : (
            <div className="space-y-8">
              {/* Results count */}
              <div className="flex items-center justify-between">
                <p className="text-sm" style={{ color: colors.textBody }}>
                  Showing <span className="font-semibold" style={{ color: colors.textHeading }}>{filteredListings.length}</span> verified partners
                </p>
                <div className="flex items-center gap-2">
                  <span className="text-xs" style={{ color: colors.textMuted }}>Switch to:</span>
                  <button
                    onClick={() => navigate(isSponsor ? '/partners/ngos' : '/partners/sponsors')}
                    className="px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200"
                    style={{ backgroundColor: colors.buttonPrimary, color: '#FFFFFF' }}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = colors.buttonHover; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = colors.buttonPrimary; }}
                  >
                    {isSponsor ? 'View NGOs' : 'View Sponsors'}
                  </button>
                </div>
              </div>

              {/* Grouped by State */}
              {Object.entries(listingsByState).sort().map(([state, stateListings]) => (
                <div key={state}>
                  <div className="flex items-center gap-2 mb-4">
                    <MapPin className="w-4 h-4" style={{ color: colors.gold }} />
                    <h2 className="font-semibold" style={{ color: colors.textHeading }}>{state}</h2>
                    <span className="text-xs" style={{ color: colors.textMuted }}>({stateListings.length})</span>
                  </div>

                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {stateListings.map((listing) => (
                      <div 
                        key={listing.id} 
                        className="rounded-xl p-5 transition-all duration-300"
                        style={{ 
                          backgroundColor: colors.cardLight, 
                          border: '1px solid rgba(151, 182, 167, 0.2)'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'translateY(-4px)';
                          e.currentTarget.style.boxShadow = '0 12px 32px rgba(0, 0, 0, 0.15)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = 'none';
                        }}
                      >
                        <div className="flex items-start justify-between gap-3 mb-3">
                          <h3 className="font-semibold line-clamp-1" style={{ color: colors.forestTeal }}>
                            {listing.orgName}
                          </h3>
                          {listing.status === 'verified' && (
                            <CheckCircle className="w-4 h-4 flex-shrink-0" style={{ color: colors.sageTeal }} />
                          )}
                        </div>

                        <div className="flex flex-wrap gap-1.5 mb-3">
                          {listing.sectors?.slice(0, 3).map((sector, idx) => (
                            <span key={idx} className="px-2 py-1 rounded text-xs" style={{ backgroundColor: 'rgba(214, 177, 94, 0.2)', color: colors.gold }}>{sector}</span>
                          ))}
                          {listing.sectors?.length > 3 && (
                            <span className="text-xs" style={{ color: colors.mutedTeal }}>+{listing.sectors.length - 3}</span>
                          )}
                        </div>

                        <div className="flex items-center gap-1 text-xs mb-4" style={{ color: colors.mutedTeal }}>
                          <MapPin className="w-3 h-3" />
                          <span>{listing.states?.join(', ') || 'Pan India'}</span>
                        </div>

                        {listing.summary && (
                          <p className="text-xs line-clamp-2 mb-4" style={{ color: colors.mutedTeal }}>{listing.summary}</p>
                        )}

                        <button 
                          onClick={() => navigate('/contact')}
                          className="w-full h-10 rounded-lg text-sm font-medium transition-all duration-200"
                          style={{ backgroundColor: colors.buttonPrimary, color: '#FFFFFF' }}
                          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = colors.buttonHover; }}
                          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = colors.buttonPrimary; }}
                        >
                          Express Interest
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 px-4" style={{ backgroundColor: colors.forestTeal }}>
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-xl font-bold mb-3" style={{ color: colors.textHeading }}>
            Want to join our verified partner network?
          </h2>
          <p className="text-sm mb-6" style={{ color: colors.textBody }}>
            Register your organization and get verified by our team.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button 
              onClick={() => navigate('/xchange/corporate/register')} 
              className="inline-flex items-center gap-2 px-6 py-3 rounded-lg font-semibold transition-all duration-200"
              style={{ backgroundColor: colors.buttonPrimary, color: '#FFFFFF' }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = colors.buttonHover; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = colors.buttonPrimary; }}
            >
              <Building2 className="w-4 h-4" />
              Register as Sponsor
            </button>
            <button 
              onClick={() => navigate('/xchange/ngo/register')} 
              className="inline-flex items-center gap-2 px-6 py-3 rounded-lg font-semibold transition-all duration-200"
              style={{ backgroundColor: 'transparent', color: '#FFFFFF', border: '2px solid rgba(255, 255, 255, 0.4)' }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = colors.buttonPrimary; e.currentTarget.style.borderColor = colors.buttonPrimary; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.4)'; }}
            >
              <Users className="w-4 h-4" />
              Register as NGO
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default VerifiedPartnersPage;
